// ─────────────────────────────────────────────────────────────
// Landing.js - Marketing landing page for Merq Vault
// ─────────────────────────────────────────────────────────────
// Full replacement. Hero is "Chaos Free Finance" with animated
// phone cycling real app screens. Adds Money Conversations,
// Debt Strategy, and multi-currency household sections.
// All em-dashes scrubbed. Italics + gradient emphasis preserved.
// AuthModal unchanged.
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { QRCodeSVG } from 'qrcode.react'

const GlobalStyle = () => (
  <style>{`
    :root {
      --plum: #1e1040;
      --plum-deep: #0a0520;
      --cream: #FAF7F2;
      --cream-2: #F2EDE3;
      --ink: #1B2B3A;
      --ink-muted: #8899AA;
      --line: #E8EDF2;
      --line-strong: #CDD5DD;
      --mint: #22C55E;
      --mint-deep: #052e16;
      --mint-soft: #E8F7F1;
      --purple: #A78BFA;
      --orange: #F59E0B;
      --pink: #EC4899;
      --blue: #60A5FA;
      --muted-light: #94A3B8;
    }

    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      color: var(--ink);
      background: var(--cream);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }

    .lp-frns { font-family: 'Fraunces', Georgia, serif; font-weight: 900; letter-spacing: -0.02em; }
    .lp-frns-italic { font-family: 'Fraunces', Georgia, serif; font-weight: 900; font-style: italic; letter-spacing: -0.02em; }

    .grad-gm {
      background: linear-gradient(135deg, var(--mint) 0%, var(--purple) 50%, var(--blue) 100%);
      -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    }
    .grad-purple-blue {
      background: linear-gradient(135deg, var(--purple) 0%, var(--blue) 100%);
      -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    }
    .grad-orange-pink {
      background: linear-gradient(135deg, var(--orange) 0%, var(--pink) 100%);
      -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    }
    .grad-mint-blue {
      background: linear-gradient(135deg, var(--mint) 0%, var(--blue) 100%);
      -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    }

    /* NAV */
    .lp-nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      padding: 16px 32px; display: flex; align-items: center; justify-content: space-between;
      transition: background 0.25s, box-shadow 0.25s, backdrop-filter 0.25s;
    }
    .lp-nav.scrolled-light { background: rgba(250, 247, 242, 0.85); backdrop-filter: blur(12px); box-shadow: 0 1px 0 rgba(0,0,0,0.05); }
    .lp-nav.scrolled-dark { background: rgba(10, 5, 32, 0.85); backdrop-filter: blur(12px); box-shadow: 0 1px 0 rgba(255,255,255,0.05); }
    .lp-nav-link { color: var(--ink); text-decoration: none; font-size: 14px; font-weight: 500; padding: 8px 14px; transition: opacity 0.2s; }
    .lp-nav-link:hover { opacity: 0.7; }
    .lp-nav-link.dark { color: rgba(255,255,255,0.85); }
    .lp-nav-cta { padding: 10px 20px; background: var(--mint); color: var(--mint-deep); border-radius: 999px; text-decoration: none; font-size: 14px; font-weight: 700; transition: transform 0.15s; display: inline-block; }
    .lp-nav-cta:hover { transform: translateY(-1px); }
    .lp-nav-login { padding: 10px 20px; background: transparent; color: var(--ink); border: 1.5px solid var(--line-strong); border-radius: 999px; cursor: pointer; font-size: 14px; font-weight: 600; transition: all 0.15s; }
    .lp-nav-login:hover { background: var(--cream-2); }
    .lp-nav-login.dark { color: rgba(255,255,255,0.9); border-color: rgba(255,255,255,0.25); }
    .lp-nav-login.dark:hover { background: rgba(255,255,255,0.06); }
    @media (max-width: 720px) {
      .lp-nav { padding: 14px 18px; }
      .lp-nav .lp-nav-link, .lp-nav .lp-nav-login { display: none; }
    }

    /* HERO */
    .lp-hero {
      min-height: 100vh;
      padding: 120px 32px 80px;
      display: flex; align-items: center; justify-content: center;
      position: relative; overflow: hidden;
      background:
        radial-gradient(circle at 18% 22%, rgba(167, 139, 250, 0.14), transparent 55%),
        radial-gradient(circle at 82% 72%, rgba(34, 197, 94, 0.12), transparent 55%),
        radial-gradient(circle at 50% 95%, rgba(96, 165, 250, 0.08), transparent 60%),
        var(--cream);
    }
    .lp-hero-grid {
      max-width: 1240px; width: 100%;
      display: grid; grid-template-columns: 1.05fr 1fr; gap: 60px; align-items: center;
      position: relative; z-index: 2;
    }
    @media (max-width: 980px) {
      .lp-hero-grid { grid-template-columns: 1fr; gap: 40px; text-align: center; }
      .lp-hero-mock { order: -1; }
    }
    .lp-hero-tag {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 7px 14px; background: rgba(167, 139, 250, 0.15);
      border-radius: 999px; font-size: 12px; font-weight: 600;
      color: #6D4FD3; letter-spacing: 0.04em; text-transform: uppercase;
      margin-bottom: 24px;
    }
    .lp-hero h1 {
      font-size: clamp(52px, 8vw, 104px); line-height: 0.92; margin: 0 0 24px;
      letter-spacing: -0.035em;
    }
    .lp-hero p.lead { font-size: 18px; color: var(--ink-muted); margin: 0 0 32px; max-width: 540px; line-height: 1.55; }
    @media (max-width: 980px) { .lp-hero p.lead { margin-left: auto; margin-right: auto; } }
    .lp-hero-buttons { display: flex; gap: 12px; flex-wrap: wrap; }
    @media (max-width: 980px) { .lp-hero-buttons { justify-content: center; } }
    .lp-hero-bullets { display: flex; gap: 20px; margin-top: 28px; flex-wrap: wrap; font-size: 13px; color: var(--ink-muted); }
    @media (max-width: 980px) { .lp-hero-bullets { justify-content: center; } }
    .lp-hero-bullets span { display: inline-flex; align-items: center; gap: 6px; }

    /* BUTTONS */
    .btn-primary {
      padding: 14px 28px; background: var(--mint); color: var(--mint-deep);
      border-radius: 999px; text-decoration: none; font-size: 15px; font-weight: 700;
      cursor: pointer; border: none; transition: transform 0.15s, box-shadow 0.15s;
      display: inline-flex; align-items: center; gap: 6px;
    }
    .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(34, 197, 94, 0.25); }
    .btn-secondary {
      padding: 14px 28px; background: transparent; color: var(--ink);
      border: 1.5px solid var(--line-strong); border-radius: 999px;
      text-decoration: none; font-size: 15px; font-weight: 600;
      cursor: pointer; transition: all 0.15s;
    }
    .btn-secondary:hover { background: var(--cream-2); }
    .btn-secondary.dark { color: rgba(255,255,255,0.9); border-color: rgba(255,255,255,0.3); }
    .btn-secondary.dark:hover { background: rgba(255,255,255,0.08); }

    /* PHONE FRAME (animated cycling screens) */
    .phone-wrap {
      position: relative;
      width: 100%; max-width: 380px; margin: 0 auto;
      aspect-ratio: 9 / 19;
    }
    .phone-glow {
      position: absolute; inset: -40px;
      background: radial-gradient(circle, rgba(167,139,250,0.25), transparent 65%);
      filter: blur(40px); z-index: 0;
      animation: phoneGlow 8s ease-in-out infinite;
    }
    @keyframes phoneGlow {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.08); }
    }
    .phone-frame {
      position: relative; z-index: 1;
      width: 100%; height: 100%;
      background: #0a0520;
      border-radius: 44px;
      padding: 10px;
      box-shadow:
        0 0 0 2px #1a0d35,
        0 30px 80px rgba(30, 16, 64, 0.35),
        0 50px 120px rgba(30, 16, 64, 0.25),
        inset 0 0 0 1px rgba(255,255,255,0.08);
    }
    .phone-screen {
      position: relative;
      width: 100%; height: 100%;
      background: var(--cream);
      border-radius: 36px;
      overflow: hidden;
    }
    .phone-notch {
      position: absolute; top: 6px; left: 50%; transform: translateX(-50%);
      width: 90px; height: 22px; background: #0a0520;
      border-radius: 0 0 14px 14px; z-index: 4;
    }
    /* Strip is 500% tall (5 panels stacked vertically), each panel = 20% = exactly 1 screen height */
    .phone-strip {
      position: absolute; top: 0; left: 0; right: 0;
      height: 500%;
      display: flex; flex-direction: column;
      animation: phoneCycle 25s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    }
    .phone-strip > div {
      width: 100%; height: 20%;
      overflow: hidden;
      display: block;
      flex-shrink: 0;
    }
    .phone-strip img {
      width: 100%; height: 100%;
      display: block; object-fit: cover; object-position: top center;
    }
    /* Each screen holds for 4s (16%), transitions for 1s (4%). Cycle = 25s total. */
    @keyframes phoneCycle {
      0%, 16%    { transform: translateY(0); }
      20%, 36%   { transform: translateY(-20%); }
      40%, 56%   { transform: translateY(-40%); }
      60%, 76%   { transform: translateY(-60%); }
      80%, 96%   { transform: translateY(-80%); }
      100%       { transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .phone-strip { animation: none; }
      .phone-glow { animation: none; }
    }

    /* SECTIONS */
    .lp-section { padding: 100px 32px; position: relative; }
    .lp-section.dark, .lp-dark { background: var(--plum-deep); color: white; }
    .lp-section.cream2 { background: var(--cream-2); }
    .lp-section.plum-grad {
      background:
        radial-gradient(circle at 20% 0%, rgba(167, 139, 250, 0.12), transparent 50%),
        radial-gradient(circle at 80% 100%, rgba(34, 197, 94, 0.08), transparent 50%),
        var(--plum-deep);
    }
    .lp-container { max-width: 1240px; margin: 0 auto; }
    .lp-eyebrow {
      display: inline-flex; align-items: center; gap: 10px;
      font-size: 12px; font-weight: 700; color: var(--mint);
      letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 12px;
    }
    .lp-eyebrow::before { content: ''; width: 30px; height: 2px; background: var(--mint); }
    .lp-h2 { font-size: clamp(36px, 5vw, 56px); line-height: 1.05; margin: 0 0 18px; letter-spacing: -0.025em; }
    .lp-section .lead { font-size: 17px; color: var(--ink-muted); margin: 0 0 48px; max-width: 640px; line-height: 1.6; }
    .lp-section.dark .lead { color: rgba(255,255,255,0.65); }

    /* REVEAL ANIMATIONS */
    .lp-reveal, .lp-reveal-left, .lp-reveal-right { opacity: 0; transition: all 0.7s ease; }
    .lp-reveal { transform: translateY(24px); }
    .lp-reveal-left { transform: translateX(-24px); }
    .lp-reveal-right { transform: translateX(24px); }
    .lp-reveal.visible, .lp-reveal-left.visible, .lp-reveal-right.visible { opacity: 1; transform: none; }

    /* MONEY CONVERSATIONS SECTION */
    .mc-section {
      background:
        radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.06), transparent 50%),
        var(--cream);
      position: relative; overflow: hidden;
    }
    .mc-grid {
      display: grid; grid-template-columns: 1fr 1.15fr; gap: 60px; align-items: center;
      margin-bottom: 80px;
    }
    @media (max-width: 980px) { .mc-grid { grid-template-columns: 1fr; gap: 48px; } }

    /* Three mobile phones in a row */
    .mc-phones {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
      align-items: center;
    }
    @media (max-width: 720px) {
      .mc-phones { grid-template-columns: 1fr; gap: 16px; max-width: 320px; margin: 0 auto; }
    }
    .mc-phone {
      position: relative;
      aspect-ratio: 9 / 19;
      background: #0a0520;
      border-radius: 28px;
      padding: 6px;
      box-shadow:
        0 0 0 1.5px #1a0d35,
        0 18px 48px rgba(30, 16, 64, 0.18),
        0 30px 70px rgba(30, 16, 64, 0.12);
    }
    .mc-phone-screen {
      width: 100%; height: 100%;
      border-radius: 22px; overflow: hidden;
      background: var(--cream);
      position: relative;
    }
    .mc-phone-screen img {
      width: 100%; height: 100%;
      object-fit: cover; object-position: top center;
      display: block;
    }
    .mc-phone.lift-1 { transform: translateY(-20px); }
    .mc-phone.lift-2 { transform: translateY(20px); }
    @media (max-width: 720px) {
      .mc-phone.lift-1, .mc-phone.lift-2 { transform: none; }
    }
    .mc-phone-label {
      position: absolute; bottom: -32px; left: 0; right: 0;
      text-align: center;
      font-size: 11px; font-weight: 700;
      color: var(--ink-muted);
      letter-spacing: 0.08em; text-transform: uppercase;
    }

    .mc-quote {
      max-width: 880px; margin: 80px auto 0;
      padding: 56px 40px;
      text-align: center;
      background: white;
      border-radius: 24px;
      border: 1px solid var(--line);
      box-shadow: 0 20px 60px rgba(30, 16, 64, 0.08);
      position: relative;
    }
    .mc-quote::before {
      content: '"'; position: absolute;
      top: -10px; left: 30px;
      font-family: 'Fraunces', Georgia, serif;
      font-size: 140px; line-height: 1;
      color: var(--mint); opacity: 0.25;
      font-style: italic; font-weight: 900;
    }
    .mc-quote-text {
      font-family: 'Fraunces', Georgia, serif;
      font-size: clamp(24px, 3.5vw, 36px);
      line-height: 1.25; font-weight: 700;
      color: var(--ink); margin: 0 0 20px;
      letter-spacing: -0.015em;
    }
    .mc-quote-attr {
      font-size: 13px; font-weight: 600;
      color: var(--ink-muted); letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    /* DEBT STRATEGY */
    .debt-section {
      background:
        radial-gradient(circle at 100% 0%, rgba(96, 165, 250, 0.05), transparent 40%),
        var(--cream-2);
    }
    .debt-grid {
      display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 60px; align-items: center;
    }
    @media (max-width: 980px) { .debt-grid { grid-template-columns: 1fr; } }
    .debt-phone {
      position: relative;
      width: 100%; max-width: 340px; margin: 0 auto;
      aspect-ratio: 9 / 19;
      background: #0a0520;
      border-radius: 32px;
      padding: 8px;
      box-shadow:
        0 0 0 2px #1a0d35,
        0 30px 60px rgba(30, 16, 64, 0.22),
        0 50px 100px rgba(30, 16, 64, 0.15);
    }
    .debt-phone-screen {
      width: 100%; height: 100%;
      border-radius: 24px; overflow: hidden;
      background: var(--cream-2);
    }
    .debt-phone-screen img {
      width: 100%; height: 100%;
      object-fit: cover; object-position: top center;
      display: block;
    }
    .debt-features { list-style: none; padding: 0; margin: 28px 0 0; }
    .debt-features li {
      display: flex; gap: 14px; align-items: flex-start;
      margin-bottom: 16px; font-size: 15px;
    }
    .debt-features .check {
      flex-shrink: 0; width: 22px; height: 22px;
      background: var(--mint); color: var(--mint-deep);
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 800;
    }
    .debt-features strong { color: var(--ink); }
    .debt-features span { color: var(--ink-muted); }

    /* HOUSEHOLDS + CURRENCY */
    .hh-section { background: var(--plum-deep); color: white; }
    .hh-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
    @media (max-width: 980px) { .hh-grid { grid-template-columns: 1fr; gap: 40px; } }
    .hh-shot {
      border-radius: 20px; overflow: hidden;
      box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.06);
      background: white;
      max-height: 540px;
    }
    .hh-shot img {
      width: 100%; height: 100%;
      display: block;
      object-fit: cover; object-position: top center;
    }
    .hh-points { list-style: none; padding: 0; margin: 28px 0 0; }
    .hh-points li {
      display: flex; gap: 14px; align-items: flex-start;
      margin-bottom: 16px; font-size: 15px;
    }
    .hh-points .check {
      flex-shrink: 0; width: 22px; height: 22px;
      background: var(--mint); color: var(--mint-deep);
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 800;
    }
    .hh-points strong { color: white; }
    .hh-points span { color: rgba(255,255,255,0.7); }

    /* CURRENCY BAND (own section) */
    .currency-section {
      background: var(--plum-deep);
      padding: 80px 32px 100px;
      border-top: 1px solid rgba(255,255,255,0.06);
    }
    .currency-band {
      max-width: 1240px; margin: 0 auto;
      display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 60px; align-items: center;
    }
    @media (max-width: 880px) {
      .currency-band { grid-template-columns: 1fr; gap: 40px; text-align: center; }
    }
    .currency-band h3 {
      font-family: 'Fraunces', Georgia, serif;
      font-size: clamp(28px, 3.5vw, 42px);
      font-weight: 900; line-height: 1.1;
      margin: 0 0 12px;
      color: white; letter-spacing: -0.02em;
    }
    .currency-band p {
      font-size: 15px; color: rgba(255,255,255,0.65);
      margin: 0 0 28px; line-height: 1.55;
    }
    .currency-strip {
      display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px;
    }
    @media (max-width: 480px) {
      .currency-strip { grid-template-columns: repeat(3, 1fr); }
    }
    .currency-chip {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 18px 8px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 12px;
      transition: transform 0.2s, border-color 0.2s, background 0.2s;
    }
    .currency-chip:hover {
      transform: translateY(-2px);
      background: rgba(255,255,255,0.06);
      border-color: rgba(34, 197, 94, 0.3);
    }
    .currency-chip .sym {
      font-family: 'Fraunces', Georgia, serif;
      font-size: 30px; font-weight: 900; line-height: 1;
      background: linear-gradient(135deg, var(--mint), var(--blue));
      -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
    }
    .currency-chip .code {
      font-size: 10px; font-weight: 800;
      color: rgba(255,255,255,0.7);
      letter-spacing: 0.12em; margin-top: 6px;
    }
    .currency-phone {
      width: 100%; max-width: 320px; margin: 0 auto;
      aspect-ratio: 9 / 19;
      background: #0a0520;
      border-radius: 32px;
      padding: 8px;
      box-shadow:
        0 0 0 2px #1a0d35,
        0 30px 60px rgba(0, 0, 0, 0.4),
        0 50px 100px rgba(0, 0, 0, 0.25);
    }
    .currency-phone-screen {
      width: 100%; height: 100%;
      border-radius: 24px; overflow: hidden;
      background: var(--cream);
    }
    .currency-phone-screen img {
      width: 100%; height: 100%;
      object-fit: cover; object-position: top center;
      display: block;
    }

    /* MIGRATE */
    .lp-migrate { display: grid; grid-template-columns: 1.1fr 1fr; gap: 60px; align-items: center; }
    @media (max-width: 980px) { .lp-migrate { grid-template-columns: 1fr; } }
    .lp-migrate-card {
      background: white; border-radius: 16px; padding: 32px;
      box-shadow: 0 12px 40px rgba(30, 16, 64, 0.08);
      border: 1px solid var(--line);
    }
    .lp-migrate-csv {
      background: var(--cream-2); border-radius: 8px; padding: 12px 14px;
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      font-size: 12px; color: var(--ink); line-height: 1.7;
      overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
    }
    .lp-migrate-arrow { text-align: center; margin: 12px 0; color: var(--mint); font-size: 24px; }
    .lp-migrate-result { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
    .lp-migrate-stat { text-align: center; padding: 10px 8px; background: var(--cream-2); border-radius: 8px; }
    .lp-migrate-stat .num { font-size: 20px; font-weight: 800; }
    .lp-migrate-stat.green .num { color: var(--mint); }
    .lp-migrate-stat.amber .num { color: var(--orange); }
    .lp-migrate-stat.gray .num { color: var(--muted-light); }
    .lp-migrate-stat .lbl { font-size: 10px; color: var(--ink-muted); margin-top: 2px; }

    .qr-card {
      margin-top: 28px;
      display: inline-flex; align-items: center; gap: 14px;
      padding: 14px 18px; background: white; border-radius: 14px;
      box-shadow: 0 8px 24px rgba(30, 16, 64, 0.08);
      border: 1px solid var(--line);
    }
    .qr-card .qr-text-title { font-size: 13px; font-weight: 700; color: var(--plum); margin-bottom: 2px; }
    .qr-card .qr-text-sub { font-size: 12px; color: var(--ink-muted); line-height: 1.5; max-width: 220px; }

    /* BENTO */
    .lp-bento { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 48px; }
    @media (max-width: 980px) { .lp-bento { grid-template-columns: 1fr; } }
    .bento-card {
      padding: 28px; border-radius: 16px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.08);
      transition: transform 0.2s, border-color 0.2s, background 0.2s;
    }
    .bento-card:hover { transform: translateY(-2px); background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.15); }
    .bento-card.wide { grid-column: span 2; }
    @media (max-width: 980px) { .bento-card.wide { grid-column: span 1; } }
    .bento-card.green { border-top: 2px solid var(--mint); }
    .bento-card.purple { border-top: 2px solid var(--purple); }
    .bento-card.orange { border-top: 2px solid var(--orange); }
    .bento-card.blue { border-top: 2px solid var(--blue); }
    .bento-icon { font-size: 28px; margin-bottom: 12px; display: block; }
    .bento-title { font-family: 'Fraunces', Georgia, serif; font-weight: 700; font-size: 22px; margin: 0 0 8px; letter-spacing: -0.01em; }
    .bento-desc { font-size: 14px; color: rgba(255,255,255,0.65); margin: 0; line-height: 1.55; }
    .bento-stat { font-size: 56px; font-weight: 900; line-height: 1; margin-bottom: 4px; }

    /* COMPARE TABLE */
    .lp-compare-table {
      width: 100%; border-collapse: collapse; margin-top: 32px;
      background: rgba(255,255,255,0.04); border-radius: 12px; overflow: hidden;
      border: 1px solid rgba(255,255,255,0.08);
    }
    .lp-compare-table th, .lp-compare-table td {
      padding: 14px 16px; text-align: left; font-size: 14px;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .lp-compare-table th { font-weight: 700; color: rgba(255,255,255,0.55); }
    .lp-compare-table th.us { color: var(--mint); font-weight: 800; }
    .lp-compare-table tr:last-child td { border-bottom: none; }
    .lp-compare-table td.feature { color: white; font-weight: 500; }
    .lp-compare-table td.us { color: var(--mint); font-weight: 700; }
    .lp-compare-table td.them { color: rgba(255,255,255,0.5); }
    @media (max-width: 720px) {
      .lp-compare-table { font-size: 12px; }
      .lp-compare-table th, .lp-compare-table td { padding: 10px 8px; }
    }

    /* PRIVACY */
    .lp-privacy { display: grid; grid-template-columns: 1.1fr 1fr; gap: 60px; align-items: center; }
    @media (max-width: 980px) { .lp-privacy { grid-template-columns: 1fr; } }
    .lp-privacy-points { list-style: none; padding: 0; margin: 32px 0 0; }
    .lp-privacy-points li { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 18px; font-size: 15px; }
    .lp-privacy-points .check {
      flex-shrink: 0; width: 22px; height: 22px;
      background: var(--mint); color: var(--mint-deep);
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
      font-size: 13px; font-weight: 800;
    }
    .lp-privacy-points strong { color: white; }
    .lp-privacy-points span { color: rgba(255,255,255,0.7); }
    .lp-vs { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .lp-vs-col { padding: 22px 20px; border-radius: 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); }
    .lp-vs-col h4 { margin: 0 0 12px; font-size: 12px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
    .lp-vs-col.us h4 { color: var(--mint); }
    .lp-vs-col.them h4 { color: var(--orange); }
    .lp-vs-col ul { list-style: none; padding: 0; margin: 0; }
    .lp-vs-col li { font-size: 13px; color: rgba(255,255,255,0.7); padding: 5px 0 5px 14px; position: relative; }
    .lp-vs-col li::before { content: '•'; position: absolute; left: 0; color: var(--mint); }
    .lp-vs-col.them li::before { color: var(--orange); }

    /* PRICING */
    .lp-pricing { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; max-width: 760px; margin: 48px auto 0; }
    @media (max-width: 720px) { .lp-pricing { grid-template-columns: 1fr; } }
    .pricing-card { padding: 32px 28px; border-radius: 18px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); position: relative; }
    .pricing-card.best { border: 1.5px solid var(--mint); }
    .pricing-card .best-tag {
      position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
      background: var(--mint); color: var(--mint-deep);
      padding: 4px 14px; border-radius: 999px;
      font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    }
    .pricing-card .label { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.55); }
    .pricing-card .price { font-size: 64px; font-weight: 900; line-height: 1; margin: 12px 0 4px; font-family: 'Fraunces', Georgia, serif; }
    .pricing-card .price .currency { font-size: 32px; vertical-align: super; }
    .pricing-card.best .price { background: linear-gradient(135deg, var(--mint), var(--blue)); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
    .pricing-card.standard .price { background: linear-gradient(135deg, var(--purple), var(--blue)); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
    .pricing-card .meta { font-size: 13px; color: rgba(255,255,255,0.55); margin-bottom: 20px; }
    .pricing-card ul { list-style: none; padding: 0; margin: 16px 0 0; }
    .pricing-card li { padding: 6px 0; font-size: 14px; color: rgba(255,255,255,0.85); }
    .pricing-card li::before { content: '✓ '; color: var(--mint); margin-right: 6px; font-weight: 800; }

    /* FOOTER */
    .lp-footer { padding: 60px 32px 40px; background: var(--cream-2); }
    .lp-footer-grid { max-width: 1240px; margin: 0 auto; display: grid; grid-template-columns: 1.5fr 1fr 1fr; gap: 48px; }
    @media (max-width: 720px) { .lp-footer-grid { grid-template-columns: 1fr; gap: 32px; } }
    .lp-footer h5 { font-size: 12px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-muted); margin: 0 0 16px; }
    .lp-footer ul { list-style: none; padding: 0; margin: 0; }
    .lp-footer li { padding: 5px 0; }
    .lp-footer a { color: var(--ink); text-decoration: none; font-size: 14px; transition: color 0.15s; }
    .lp-footer a:hover { color: var(--mint); }
    .lp-footer-tag { font-size: 14px; color: var(--ink-muted); margin: 12px 0 0; line-height: 1.6; }
    .lp-footer-flex {
      max-width: 1240px; margin: 32px auto 0; padding-top: 24px;
      border-top: 1px solid var(--line);
      display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap;
      font-size: 12px; color: var(--ink-muted);
    }
  `}</style>
)

function AuthModal({ mode: initialMode, onClose, onSuccess }) {
  const [mode, setMode] = useState(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => { setMode(initialMode) }, [initialMode])

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password })
    if (err) { setError(err.message); setLoading(false); return }
    if (data.session) localStorage.setItem('mv_refresh_token', data.session.refresh_token)
    onSuccess()
  }

  const handleSignUp = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    const currencyMap = {
      USD: { symbol: '$', locale: 'en-US' },
      ILS: { symbol: '₪', locale: 'he-IL' },
      EUR: { symbol: '€', locale: 'de-DE' },
      GBP: { symbol: '£', locale: 'en-GB' },
      MXN: { symbol: '$', locale: 'es-MX' },
    }
    const cur = currencyMap[currency] || currencyMap.USD
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { data: {
        full_name: fullName,
        language: 'en',
        currency_code: currency,
        currency_symbol: cur.symbol,
        currency_locale: cur.locale,
      } },
    })
    if (err) { setError(err.message); setLoading(false); return }
    if (data?.user) {
      await supabase.from('profiles').update({
        currency_code: currency,
        currency_symbol: cur.symbol,
        currency_locale: cur.locale,
      }).eq('id', data.user.id)
    }
    setConfirmed(true); setLoading(false)
  }

  const handleForgot = async () => {
    if (!email) { setError('Enter your email address first.'); return }
    setLoading(true)
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` })
    if (err) { setError(err.message) } else { alert(`Password reset link sent to ${email}`) }
    setLoading(false)
  }

  const inp = { width:'100%', padding:'11px 12px', borderRadius:'8px', border:'1.5px solid #E8EDF2', fontSize:'14px', outline:'none', boxSizing:'border-box', color:'#1B2B3A' }
  const lbl = { display:'block', fontSize:'13px', fontWeight:'500', color:'#1B2B3A', marginBottom:'5px' }

  return (
    <div style={{ position:'fixed',inset:0,zIndex:1000,background:'rgba(10,20,30,0.75)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px' }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background:'#fff',borderRadius:'20px',padding:'36px 32px',width:'100%',maxWidth:'420px',position:'relative',boxShadow:'0 32px 80px rgba(0,0,0,0.3)' }}>
        <button onClick={onClose} style={{ position:'absolute',top:'16px',right:'16px',background:'#F7F9FC',border:'1px solid #E8EDF2',borderRadius:'50%',width:'32px',height:'32px',fontSize:'14px',color:'#8899AA',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
        <div style={{ textAlign:'center',fontSize:'26px',fontWeight:'900',marginBottom:'20px',fontFamily:"'Fraunces',serif",fontStyle:'italic' }}>
          <span style={{ color:'#1B2B3A',fontStyle:'normal' }}>Merq</span><span style={{ color:'#22C55E' }}> Vault</span>
        </div>
        {confirmed ? (
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:'48px',marginBottom:'16px' }}>✉️</div>
            <div style={{ fontSize:'20px',fontWeight:'700',color:'#1B2B3A',marginBottom:'10px' }}>Check your email!</div>
            <div style={{ fontSize:'14px',color:'#8899AA',lineHeight:'1.6' }}>We sent a confirmation link to <strong>{email}</strong>.</div>
            <div style={{ fontSize:'13px',color:'#8899AA',marginTop:'14px',lineHeight:'1.5' }}>
              Didn't get it? Check spam, or{' '}
              <button
                type="button"
                onClick={async () => {
                  setLoading(true); setError('')
                  const { error: err } = await supabase.auth.resend({ type: 'signup', email })
                  setLoading(false)
                  if (err) {
                    setError(err.message)
                  } else {
                    alert(`Confirmation email resent to ${email}`)
                  }
                }}
                disabled={loading}
                style={{ background:'none',border:'none',color:'#22C55E',cursor:'pointer',fontWeight:'500',padding:0,fontSize:'13px',textDecoration:'underline' }}
              >
                resend it
              </button>.
            </div>
            {error && <div style={{ background:'#FDECEA',color:'#A32D2D',padding:'10px 12px',borderRadius:'8px',fontSize:'13px',marginTop:'12px' }}>{error}</div>}
            <button onClick={() => { setConfirmed(false); setMode('login') }} style={{ display:'block',width:'100%',marginTop:'20px',padding:'12px',background:'#22C55E',color:'#052e16',border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:'700',cursor:'pointer' }}>Go to sign in →</button>
          </div>
        ) : (
          <>
            <div style={{ display:'flex',background:'#F7F9FC',borderRadius:'10px',padding:'4px',marginBottom:'20px',gap:'4px' }}>
              {['login','signup'].map(m => (
                <button key={m} onClick={() => setMode(m)} style={{ flex:1,padding:'10px',borderRadius:'8px',border:'none',background:mode===m?'#fff':'transparent',fontSize:'14px',fontWeight:'500',color:mode===m?'#1B2B3A':'#8899AA',cursor:'pointer',boxShadow:mode===m?'0 1px 4px rgba(0,0,0,0.1)':'none',transition:'all 0.2s' }}>
                  {m==='login'?'Log in':'Start free trial'}
                </button>
              ))}
            </div>
            {mode==='signup' && <div style={{ background:'#E8F7F1',color:'#0F6E56',padding:'8px 12px',borderRadius:'8px',fontSize:'13px',textAlign:'center',marginBottom:'16px',fontWeight:'500' }}>✓ 45 days free · No credit card required</div>}
            <form onSubmit={mode==='login'?handleLogin:handleSignUp}>
              {mode==='signup' && <div style={{ marginBottom:'14px' }}><label style={lbl}>Full name</label><input style={inp} type="text" placeholder="Your name" value={fullName} onChange={e=>setFullName(e.target.value)} required /></div>}
              {mode==='signup' && <div style={{ marginBottom:'14px' }}><label style={lbl}>Currency</label><select style={inp} value={currency} onChange={e=>setCurrency(e.target.value)} required><option value="USD">$ US Dollar</option><option value="ILS">₪ Israeli Shekel</option><option value="EUR">€ Euro</option><option value="GBP">£ British Pound</option><option value="MXN">$ Mexican Peso</option></select></div>}
              <div style={{ marginBottom:'14px' }}><label style={lbl}>Email</label><input style={inp} type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} required /></div>
              <div style={{ marginBottom:'14px' }}><label style={lbl}>Password</label><input style={inp} type="password" placeholder={mode==='signup'?'Min 6 characters':'••••••••'} value={password} onChange={e=>setPassword(e.target.value)} required minLength={mode==='signup'?6:undefined} /></div>
              {error && <div style={{ background:'#FDECEA',color:'#A32D2D',padding:'10px 12px',borderRadius:'8px',fontSize:'13px',marginBottom:'14px' }}>{error}</div>}
              <button type="submit" disabled={loading} style={{ width:'100%',padding:'13px',background:'#22C55E',color:'#052e16',border:'none',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer',marginTop:'4px' }}>
                {loading?(mode==='login'?'Signing in...':'Creating account...'):(mode==='login'?'Sign in →':'Start free trial →')}
              </button>
            </form>
            {mode==='login' && <button onClick={handleForgot} disabled={loading} style={{ width:'100%',background:'none',border:'none',color:'#8899AA',fontSize:'13px',cursor:'pointer',marginTop:'10px',textAlign:'center',padding:'4px' }}>Forgot password?</button>}
            {mode==='signup' && <div style={{ fontSize:'12px',color:'#8899AA',textAlign:'center',marginTop:'12px',lineHeight:'1.6' }}>By signing up you agree to our <a href="https://getmerq.com/terms.html" target="_blank" rel="noopener noreferrer" style={{color:'#22C55E'}}>Terms</a> and <a href="https://getmerq.com/privacy.html" target="_blank" rel="noopener noreferrer" style={{color:'#22C55E'}}>Privacy Policy</a>.</div>}
            <div style={{ textAlign:'center',marginTop:'14px',fontSize:'13px',color:'#8899AA' }}>
              {mode==='login'?<>Don't have an account? <button onClick={()=>setMode('signup')} style={{background:'none',border:'none',color:'#22C55E',fontSize:'13px',cursor:'pointer',fontWeight:'500'}}>Start free trial</button></>:<>Already have an account? <button onClick={()=>setMode('login')} style={{background:'none',border:'none',color:'#22C55E',fontSize:'13px',cursor:'pointer',fontWeight:'500'}}>Log in</button></>}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function Landing({ session }) {
  const navigate = useNavigate()
  const [authModal, setAuthModal] = useState(null)
  const [navClass, setNavClass] = useState('')
  const [linkStyle, setLinkStyle] = useState('light')

  const openLogin = () => setAuthModal('login')
  const openSignup = () => setAuthModal('signup')
  const closeModal = () => setAuthModal(null)
  const handleAuthSuccess = () => { closeModal(); navigate('/') }
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

  useEffect(() => {
    const updateNav = () => {
      const darkZone = document.querySelector('.lp-dark')
      if (!darkZone) return
      const scrollY = window.scrollY
      const darkTop = darkZone.offsetTop
      const darkBottom = darkTop + darkZone.offsetHeight
      const inDark = scrollY + 60 > darkTop && scrollY + 60 < darkBottom
      if (scrollY > 60) {
        setNavClass(inDark ? 'scrolled-dark' : 'scrolled-light')
        setLinkStyle(inDark ? 'dark' : 'light')
      } else { setNavClass(''); setLinkStyle('light') }
    }
    window.addEventListener('scroll', updateNav)
    return () => window.removeEventListener('scroll', updateNav)
  }, [])

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) { setTimeout(() => e.target.classList.add('visible'), i * 100); obs.unobserve(e.target) }
      })
    }, { threshold: 0.08 })
    document.querySelectorAll('.lp-reveal,.lp-reveal-left,.lp-reveal-right').forEach(el => obs.observe(el))
    return () => obs.disconnect()
  }, [])

  return (
    <>
      <GlobalStyle />

      {/* NAV */}
      <nav className={`lp-nav ${navClass}`}>
        <div style={{ fontSize: '22px', fontWeight: 900, fontFamily: "'Fraunces',serif" }}>
          <span style={{ color: linkStyle === 'dark' ? 'white' : 'var(--plum)' }}>Merq</span>
          <span style={{ color: 'var(--mint)', fontStyle: 'italic' }}> Vault</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => scrollTo('conversations')} className={`lp-nav-link ${linkStyle === 'dark' ? 'dark' : ''}`} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Conversations</button>
          <button onClick={() => scrollTo('features')} className={`lp-nav-link ${linkStyle === 'dark' ? 'dark' : ''}`} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Features</button>
          <button onClick={() => scrollTo('migrate')} className={`lp-nav-link ${linkStyle === 'dark' ? 'dark' : ''}`} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Switch in 5 min</button>
          <button onClick={() => scrollTo('pricing')} className={`lp-nav-link ${linkStyle === 'dark' ? 'dark' : ''}`} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Pricing</button>
          <button onClick={openLogin} className={`lp-nav-login ${linkStyle === 'dark' ? 'dark' : ''}`}>Log in</button>
          <button onClick={openSignup} className="lp-nav-cta">Start free →</button>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-grid">
          <div>
            <div className="lp-hero-tag">
              <span>●</span> Privacy-first · No bank login · No data selling
            </div>
            <h1 className="lp-frns">
              Chaos Free <span className="grad-gm lp-frns-italic">Finance.</span>
            </h1>
            <p className="lead">
              The privacy-first finance app for one or two. Budget, track net worth, kill debt, and actually talk about money with your partner. <strong>No bank login. No data selling. Ever.</strong>
            </p>
            <div className="lp-hero-buttons">
              <button onClick={openSignup} className="btn-primary">Start free for 45 days →</button>
              <button onClick={() => scrollTo('conversations')} className="btn-secondary">See how it works</button>
            </div>
            <div className="lp-hero-bullets">
              <span>✓ No credit card</span>
              <span>✓ No bank access</span>
              <span>✓ $59/year for the household</span>
            </div>
          </div>
          <div className="lp-hero-mock">
            <div className="phone-wrap">
              <div className="phone-glow" />
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="phone-notch" />
                  <div className="phone-strip">
                    <div><img src="/screenshots/m-thismonth.jpeg" alt="This month dashboard" /></div>
                    <div><img src="/screenshots/m-plan.jpeg" alt="Plan page" /></div>
                    <div><img src="/screenshots/m-networth.jpeg" alt="Net worth" /></div>
                    <div><img src="/screenshots/m-history-conversation.jpeg" alt="Money conversation" /></div>
                    <div><img src="/screenshots/m-debts-top.jpeg" alt="Debt strategy" /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MONEY CONVERSATIONS */}
      <section id="conversations" className="lp-section mc-section">
        <div className="lp-container">
          <div className="mc-grid">
            <div className="lp-reveal-left">
              <div className="lp-eyebrow">The wedge feature</div>
              <h2 className="lp-h2 lp-frns">
                The money talk, <span className="grad-mint-blue lp-frns-italic">without the fight.</span>
              </h2>
              <p className="lead">
                Pick a few questions. You both answer privately. Then reveal together. End with one small thing you'll actually do this month. It's the conversation you've been putting off, made simple.
              </p>
              <ul className="debt-features">
                <li>
                  <span className="check">✓</span>
                  <div><strong>Answer privately first.</strong> <span>No anchoring, no influencing. Your answer is yours until you both reveal.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>Reveal together.</strong> <span>Tap when you're ready. See where you align, and where you don't.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>End with one commitment.</strong> <span>One small thing you'll try together this month. We track it for you.</span></div>
                </li>
              </ul>
              <div style={{ marginTop: '32px' }}>
                <button onClick={openSignup} className="btn-primary">Try a check-in free →</button>
              </div>
            </div>
            <div className="mc-phones lp-reveal-right">
              <div className="mc-phone lift-1">
                <div className="mc-phone-screen">
                  <img src="/screenshots/m-pick-reflect-share.jpeg" alt="Pick your questions" />
                </div>
              </div>
              <div className="mc-phone">
                <div className="mc-phone-screen">
                  <img src="/screenshots/m-history-disagreement.jpeg" alt="Reveal answers, even when you disagree" />
                </div>
              </div>
              <div className="mc-phone lift-2">
                <div className="mc-phone-screen">
                  <img src="/screenshots/m-history-aligned.jpeg" alt="Conversation history" />
                </div>
              </div>
            </div>
          </div>

          {/* Beta tester quote */}
          <div className="mc-quote lp-reveal">
            <p className="mc-quote-text">
              I love this. <span className="lp-frns-italic">I absolutely love this.</span> This is the best feature of the app.
            </p>
            <div className="mc-quote-attr">Beta tester · First session, no instructions given</div>
          </div>
        </div>
      </section>

      {/* DEBT STRATEGY */}
      <section className="lp-section debt-section">
        <div className="lp-container">
          <div className="debt-grid">
            <div className="lp-reveal-left">
              <div className="lp-eyebrow" style={{ color: 'var(--blue)' }}>Debt, handled</div>
              <div className="lp-eyebrow" style={{ display: 'none' }} />
              <h2 className="lp-h2 lp-frns">
                Pay it off <span className="grad-purple-blue lp-frns-italic">on purpose.</span>
              </h2>
              <p className="lead">
                Plug in your debts. Pick avalanche or snowball. Drag the slider to see what an extra hundred bucks a month actually does. The whole math problem, on one page.
              </p>
              <ul className="debt-features">
                <li>
                  <span className="check">✓</span>
                  <div><strong>Avalanche or snowball.</strong> <span>Highest rate first or smallest balance first. Toggle and compare in real time.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>Live payoff math.</strong> <span>Drag a slider for extra payments. See total interest saved and months knocked off the calendar.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>Editable schedule.</strong> <span>Spreadsheet-style table with overrides. Got a windfall in March? Drop it in.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>Rolls into your plan.</strong> <span>Monthly debt payments auto-allocate in your budget. No double-tracking.</span></div>
                </li>
              </ul>
            </div>
            <div className="debt-phone lp-reveal-right">
              <div className="debt-phone-screen">
                <img src="/screenshots/m-debts-top.jpeg" alt="Debt strategy planner" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOUSEHOLDS */}
      <section className="lp-section hh-section lp-dark">
        <div className="lp-container">
          <div className="hh-grid">
            <div className="lp-reveal-left">
              <div className="lp-eyebrow">Built for two. Or one.</div>
              <h2 className="lp-h2 lp-frns">
                Two logins. <span className="grad-gm lp-frns-italic">One picture.</span>
              </h2>
              <p className="lead">
                Each partner has their own account. Both see the same plan, the same net worth, the same goals. No shared passwords. No per-seat fees. Solo? It works the same way, just with one login.
              </p>
              <ul className="hh-points">
                <li>
                  <span className="check">✓</span>
                  <div><strong>Shared plan and spending.</strong> <span>Both partners see the same budget, the same transactions, the same goals.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>Combined net worth.</strong> <span>His accounts, her accounts, joint accounts. All in one view.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>One subscription, two people.</strong> <span>$59/year covers the household. Solo or shared, same price.</span></div>
                </li>
              </ul>
            </div>
            <div className="hh-shot lp-reveal-right">
              <img src="/screenshots/app-settings.png" alt="Shared finances and household settings" />
            </div>
          </div>
        </div>
      </section>

      {/* CURRENCY BAND (its own moment) */}
      <section className="currency-section lp-dark">
        <div className="currency-band">
          <div className="lp-reveal-left">
            <div className="lp-eyebrow">Built anywhere</div>
            <h3 className="lp-frns">
              Five currencies. <span className="grad-mint-blue lp-frns-italic">Same app.</span>
            </h3>
            <p>
              Most US finance apps stop at the dollar. We didn't. Whether you're tracking pesos, pounds, euros, shekels, or dollars, the app speaks your money.
            </p>
            <div className="currency-strip">
              <div className="currency-chip"><span className="sym">$</span><span className="code">USD</span></div>
              <div className="currency-chip"><span className="sym">₪</span><span className="code">ILS</span></div>
              <div className="currency-chip"><span className="sym">€</span><span className="code">EUR</span></div>
              <div className="currency-chip"><span className="sym">£</span><span className="code">GBP</span></div>
              <div className="currency-chip"><span className="sym">$</span><span className="code">MXN</span></div>
            </div>
          </div>
          <div className="currency-phone lp-reveal-right">
            <div className="currency-phone-screen">
              <img src="/screenshots/m-currency-picker.jpeg" alt="Currency picker on iPhone" />
            </div>
          </div>
        </div>
      </section>

      {/* MIGRATE */}
      <section id="migrate" className="lp-section cream2">
        <div className="lp-container">
          <div className="lp-migrate">
            <div className="lp-reveal-left">
              <div className="lp-eyebrow" style={{ color: 'var(--orange)' }}>Switch in 5 minutes</div>
              <h2 className="lp-h2 lp-frns">
                Bring your data from <span className="grad-orange-pink lp-frns-italic">anywhere.</span>
              </h2>
              <p className="lead">
                Drop in a CSV from Mint, Monarch, YNAB, or any bank. We auto-match merchants to categories, skip duplicates, and create historical plans on the fly. No copy-paste. No starting from scratch.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '24px 0 0' }}>
                <li style={{ padding: '6px 0', fontSize: '15px' }}><span style={{ color: 'var(--mint)', marginRight: '8px', fontWeight: 800 }}>✓</span> Auto-detects column names from any export</li>
                <li style={{ padding: '6px 0', fontSize: '15px' }}><span style={{ color: 'var(--mint)', marginRight: '8px', fontWeight: 800 }}>✓</span> Auto-categorizes based on merchant memory</li>
                <li style={{ padding: '6px 0', fontSize: '15px' }}><span style={{ color: 'var(--mint)', marginRight: '8px', fontWeight: 800 }}>✓</span> Skips duplicates, creates missing months</li>
                <li style={{ padding: '6px 0', fontSize: '15px' }}><span style={{ color: 'var(--mint)', marginRight: '8px', fontWeight: 800 }}>✓</span> Years of history imported in one click</li>
              </ul>
              <div className="qr-card">
                <QRCodeSVG value="https://merqvault.com" size={72} level="M" bgColor="#FFFFFF" fgColor="#1e1040" />
                <div>
                  <div className="qr-text-title">Open on your phone</div>
                  <div className="qr-text-sub">Scan to add to home screen. Works like a native app, no App Store needed.</div>
                </div>
              </div>
            </div>
            <div className="lp-migrate-card lp-reveal-right">
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: '10px' }}>Your CSV</div>
              <div className="lp-migrate-csv">Date,Description,Amount<br/>02/14,Kroger,-87.43<br/>02/14,Starbucks,-6.75<br/>02/15,Shell,-52.18<br/>...</div>
              <div className="lp-migrate-arrow">↓</div>
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: '10px' }}>Imported &amp; categorized</div>
              <div className="lp-migrate-result">
                <div className="lp-migrate-stat green"><div className="num">10</div><div className="lbl">Ready</div></div>
                <div className="lp-migrate-stat amber"><div className="num">2</div><div className="lbl">To review</div></div>
                <div className="lp-migrate-stat gray"><div className="num">0</div><div className="lbl">Duplicates</div></div>
                <div className="lp-migrate-stat gray"><div className="num">0</div><div className="lbl">Invalid</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="lp-section dark lp-dark plum-grad">
        <div className="lp-container">
          <div className="lp-eyebrow lp-reveal">Everything you need</div>
          <h2 className="lp-h2 lp-frns lp-reveal">
            Built different. <br/><span className="grad-gm lp-frns-italic">On purpose.</span>
          </h2>
          <p className="lead lp-reveal">
            Privacy-first. Household-aware. Every feature solves a real problem.
          </p>
          <div className="lp-bento">
            <div className="bento-card green wide lp-reveal">
              <span className="bento-icon">💬</span>
              <h3 className="bento-title">Money <span style={{ color: 'var(--mint)' }}>conversations</span></h3>
              <p className="bento-desc">Pick questions. Answer privately. Reveal together. End with one concrete commitment. The wedge feature, and the one beta testers won't shut up about.</p>
            </div>
            <div className="bento-card blue lp-reveal">
              <span className="bento-icon">💳</span>
              <h3 className="bento-title">Debt strategy <span style={{ color: 'var(--blue)' }}>planner</span></h3>
              <p className="bento-desc">Avalanche or snowball. Live payoff math. Editable schedule. The whole problem on one page.</p>
            </div>
            <div className="bento-card green lp-reveal">
              <span className="bento-icon">💰</span>
              <h3 className="bento-title">Budget that <span style={{ color: 'var(--mint)' }}>actually balances</span></h3>
              <p className="bento-desc">Set income, allocate to categories. Green when on track, orange when you need to slow down.</p>
            </div>
            <div className="bento-card purple lp-reveal">
              <span className="bento-icon">🏦</span>
              <h3 className="bento-title">Net worth, <span style={{ color: 'var(--purple)' }}>no bank needed</span></h3>
              <p className="bento-desc">Add accounts manually. Savings, investments, home, car. Liabilities included. No Plaid. Ever.</p>
            </div>
            <div className="bento-card orange lp-reveal">
              <span className="bento-icon">🗓️</span>
              <h3 className="bento-title">Bills &amp; <span style={{ color: 'var(--orange)' }}>reminders</span></h3>
              <p className="bento-desc">Track every recurring bill. Orange alerts before it's due. Auto-matches transactions when you log them.</p>
            </div>
            <div className="bento-card orange wide lp-reveal">
              <span className="bento-icon">🧾</span>
              <h3 className="bento-title">AI receipt scanner, <span style={{ color: 'var(--orange)' }}>built for tax time</span></h3>
              <p className="bento-desc">Point your camera. Claude AI reads the merchant, amount, and date. Mark as business and the receipt saves itself. Download a branded PDF at month end with every expense and receipt attached.</p>
            </div>
            <div className="bento-card blue lp-reveal">
              <span className="bento-icon">📥</span>
              <h3 className="bento-title">CSV import, <span style={{ color: 'var(--blue)' }}>any source</span></h3>
              <p className="bento-desc">Mint, Monarch, YNAB, raw bank exports. Drop in a CSV. We auto-categorize, dedupe, and create plans for missing months.</p>
            </div>
            <div className="bento-card purple lp-reveal">
              <span className="bento-icon">🎯</span>
              <h3 className="bento-title">Savings goals, <span style={{ color: 'var(--purple)' }}>tracked</span></h3>
              <p className="bento-desc">Name it, set a target, pick a date. Purple progress bars. Share goals with your household.</p>
            </div>
            <div className="bento-card green lp-reveal">
              <span className="bento-icon">📊</span>
              <h3 className="bento-title">Reports &amp; <span style={{ color: 'var(--mint)' }}>tax exports</span></h3>
              <p className="bento-desc">Multi-month summaries. Business expense PDFs with receipts attached. Built so your accountant doesn't email you back.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section className="lp-section dark lp-dark" style={{ paddingTop: 0 }}>
        <div className="lp-container">
          <div className="lp-privacy">
            <div className="lp-reveal-left">
              <div className="lp-eyebrow">Privacy-first</div>
              <h2 className="lp-h2 lp-frns">
                We make money <span className="grad-gm lp-frns-italic">one way only.</span>
              </h2>
              <p className="lead">
                When you love it enough to subscribe. No ads. No data selling. No Plaid. Your financial life stays yours.
              </p>
              <ul className="lp-privacy-points">
                <li>
                  <span className="check">✓</span>
                  <div><strong>No bank connection. Ever.</strong> <span>Manual entry or CSV import. The friction is the feature.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>No Plaid. No aggregators.</strong> <span>Your credentials never leave your bank.</span></div>
                </li>
                <li>
                  <span className="check">✓</span>
                  <div><strong>No ads. No data selling.</strong> <span>One revenue stream: your subscription.</span></div>
                </li>
              </ul>
            </div>
            <div className="lp-reveal-right">
              <div style={{ textAlign: 'center', padding: '32px', background: 'rgba(255,255,255,0.04)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '12px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔐</div>
                <div className="lp-frns" style={{ fontSize: '22px', fontWeight: 700, color: 'white' }}>Your bank stays yours.</div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', marginTop: '4px' }}>We never ask. We never will.</div>
              </div>
              <div className="lp-vs">
                <div className="lp-vs-col us">
                  <h4>Merq Vault</h4>
                  <ul>
                    <li>Manual + CSV</li>
                    <li>No Plaid</li>
                    <li>No data sold</li>
                    <li>No ads</li>
                    <li>You pay us</li>
                  </ul>
                </div>
                <div className="lp-vs-col them">
                  <h4>Free apps</h4>
                  <ul>
                    <li>Bank login required</li>
                    <li>Data aggregators</li>
                    <li>Data sold</li>
                    <li>Ad-supported</li>
                    <li>You're the product</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARE TABLE */}
      <section className="lp-section dark lp-dark" style={{ paddingTop: 0 }}>
        <div className="lp-container">
          <div className="lp-eyebrow lp-reveal">How we compare</div>
          <h2 className="lp-h2 lp-frns lp-reveal">
            Half the price. <br/><span className="grad-purple-blue lp-frns-italic">Built for everyone.</span>
          </h2>
          <table className="lp-compare-table lp-reveal">
            <thead>
              <tr>
                <th></th>
                <th className="us">Merq Vault</th>
                <th>YNAB</th>
                <th>Monarch</th>
                <th>Mint*</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="feature">Price for a household</td>
                <td className="us">$59/year</td>
                <td className="them">$109/year</td>
                <td className="them">$100/year</td>
                <td className="them">Shut down</td>
              </tr>
              <tr>
                <td className="feature">Money conversations</td>
                <td className="us">✓ Built-in</td>
                <td className="them">✗ No</td>
                <td className="them">✗ No</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">No bank login</td>
                <td className="us">✓ Never</td>
                <td className="them">Optional</td>
                <td className="them">✗ Required</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">CSV import</td>
                <td className="us">✓ Smart</td>
                <td className="them">Manual mapping</td>
                <td className="them">Limited</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">Multi-currency</td>
                <td className="us">✓ 5 currencies</td>
                <td className="them">USD only</td>
                <td className="them">USD only</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">Shared finances</td>
                <td className="us">✓ Yes</td>
                <td className="them">✓ Yes</td>
                <td className="them">Partial</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">Business expense PDF</td>
                <td className="us">✓ Yes</td>
                <td className="them">✗ No</td>
                <td className="them">✗ No</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">Debt strategy planner</td>
                <td className="us">✓ Built-in</td>
                <td className="them">Add-on</td>
                <td className="them">Basic</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">No data selling</td>
                <td className="us">✓ Never</td>
                <td className="them">✓ Yes</td>
                <td className="them">✓ Yes</td>
                <td className="them">n/a</td>
              </tr>
              <tr>
                <td className="feature">Free trial</td>
                <td className="us">45 days, no card</td>
                <td className="them">34 days</td>
                <td className="them">7 days</td>
                <td className="them">n/a</td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: '14px', fontSize: '12px', color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}>
            * Mint shut down January 2024. YNAB and Monarch pricing shown is per household, not per person. Pricing as of November 2026. Verify independently.
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lp-section dark lp-dark" style={{ paddingTop: 0 }}>
        <div className="lp-container" style={{ textAlign: 'center' }}>
          <div className="lp-eyebrow lp-reveal" style={{ justifyContent: 'center', display: 'inline-flex' }}>One price</div>
          <h2 className="lp-h2 lp-frns lp-reveal">
            One price. <br/><span className="grad-gm lp-frns-italic">Everything included.</span>
          </h2>
          <p className="lead lp-reveal" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            No per-seat fees. No hidden costs. Solo or with a partner, same price.
          </p>
          <div className="lp-pricing">
            <div className="pricing-card best lp-reveal">
              <span className="best-tag">Best value</span>
              <div className="label">Annual</div>
              <div className="price"><span className="currency">$</span>59</div>
              <div className="meta">per year · $4.92/mo · Save 30%</div>
              <ul>
                <li>Solo or shared household</li>
                <li>Money conversations</li>
                <li>Debt strategy planner</li>
                <li>Unlimited receipt storage</li>
                <li>Full budgeting &amp; net worth</li>
                <li>CSV import from any source</li>
                <li>Business expense PDF reports</li>
                <li>Priority support</li>
              </ul>
            </div>
            <div className="pricing-card standard lp-reveal">
              <div className="label">Monthly</div>
              <div className="price"><span className="currency">$</span>7</div>
              <div className="meta">per month · Cancel anytime</div>
              <ul>
                <li>Everything in Annual</li>
                <li>No long-term commitment</li>
                <li>Switch to Annual anytime</li>
              </ul>
            </div>
          </div>
          <div style={{ marginTop: '36px' }}>
            <button onClick={openSignup} className="btn-primary">Start your 45-day free trial →</button>
            <div style={{ marginTop: '14px', fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
              No credit card · Cancel anytime · Secure checkout by Paddle
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="lp-section">
        <div className="lp-container" style={{ textAlign: 'center' }}>
          <h2 className="lp-h2 lp-frns lp-reveal">
            Stop guessing. <br/><span className="grad-gm lp-frns-italic">Start knowing.</span>
          </h2>
          <p className="lead lp-reveal" style={{ margin: '0 auto 32px' }}>
            45 days free. No credit card. No bank access. Just clarity.
          </p>
          <button onClick={openSignup} className="btn-primary lp-reveal">Start free for 45 days →</button>
          <div className="lp-reveal" style={{ marginTop: '12px', fontSize: '13px', color: 'var(--ink-muted)' }}>
            No credit card · Cancel anytime · Secure checkout by Paddle
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div>
            <div style={{ fontSize: '20px', fontWeight: 900, fontFamily: "'Fraunces',serif" }}>
              <span style={{ color: 'var(--plum)' }}>Merq</span>
              <span style={{ color: 'var(--mint)', fontStyle: 'italic' }}> Vault</span>
            </div>
            <p className="lp-footer-tag">
              Chaos Free Finance.<br/>Privacy-first. Built for one or two. Made by humans, not banks.
            </p>
          </div>
          <div>
            <h5>Product</h5>
            <ul>
              <li><button onClick={() => scrollTo('conversations')} style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontSize: '14px' }}>Money conversations</button></li>
              <li><button onClick={() => scrollTo('features')} style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontSize: '14px' }}>Features</button></li>
              <li><button onClick={() => scrollTo('migrate')} style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontSize: '14px' }}>Switch in 5 min</button></li>
              <li><button onClick={() => scrollTo('pricing')} style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontSize: '14px' }}>Pricing</button></li>
              <li><button onClick={openSignup} style={{ background: 'none', border: 'none', color: 'var(--ink)', cursor: 'pointer', padding: 0, fontSize: '14px' }}>Start free trial</button></li>
            </ul>
          </div>
          <div>
            <h5>Company</h5>
            <ul>
              <li><a href="https://getmerq.com" target="_blank" rel="noopener noreferrer">Merq Software</a></li>
              <li><a href="https://getmerq.com/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              <li><a href="https://getmerq.com/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a></li>
              <li><a href="mailto:hello@merqvault.com">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-flex">
          <span>© {new Date().getFullYear()} Merq Software. Made in Texas.</span>
          <span>Your bank stays yours. We never ask. We never will.</span>
        </div>
      </footer>

      {authModal && <AuthModal mode={authModal} onClose={closeModal} onSuccess={handleAuthSuccess} />}
    </>
  )
}
